import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Gift, Plus, Phone, Lightning, Eye, EyeSlash, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';

const TYPES = [
  { key: 'gift_card',   label: 'Gift Card Vendor',   icon: Gift,       color: 'fuchsia' },
  { key: 'recharge',    label: 'Recharge Vendor',    icon: Phone,      color: 'indigo' },
  { key: 'electricity', label: 'Electricity Vendor', icon: Lightning,  color: 'amber' },
  { key: 'card',        label: 'Card Vendor',        icon: CreditCard, color: 'emerald' },
];

function fmt(n) {
  if (typeof n !== 'number') return n;
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function VendorsSection({ isReadOnly }) {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    vendor_type: 'gift_card', discount_percent: 1.5, initial_balance: 0,
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get('/vendors/');
      setVendors(r.data || []);
    } catch {/* ignore */}
  }, []);

  useEffect(() => { load(); }, [load]);

  const createVendor = async () => {
    if (!form.name || !form.email || !form.password) {
      toast.error('Name, email, password zaroori'); return;
    }
    setCreating(true);
    try {
      await api.post('/vendors/create', {
        ...form,
        discount_percent: Number(form.discount_percent) || 0,
        initial_balance: Number(form.initial_balance) || 0,
      });
      toast.success('Vendor created');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', phone: '', vendor_type: 'gift_card', discount_percent: 1.5, initial_balance: 0 });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Create failed');
    } finally { setCreating(false); }
  };

  const typeChip = (vt) => {
    const t = TYPES.find((x) => x.key === vt) || TYPES[0];
    return (
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded bg-${t.color}-100 text-${t.color}-700`}>
        {t.label.replace(' Vendor', '')}
      </span>
    );
  };

  return (
    <div className="card-flat" data-testid="vendors-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Gift size={22} className="text-fuchsia-600" />
          Vendors
          <span className="text-xs bg-fuchsia-100 text-fuchsia-700 px-2 py-0.5 rounded">Gift Card / Recharge / Electricity / Card Vendor</span>
        </h2>
        {!isReadOnly && (
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white gap-2" data-testid="add-vendor-btn">
                <Plus size={18} /> Add Vendor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Create Vendor</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div>
                  <Label>Vendor Type</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                    {TYPES.map((t) => {
                      const Icon = t.icon;
                      const active = form.vendor_type === t.key;
                      return (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setForm({ ...form, vendor_type: t.key })}
                          data-testid={`vtype-${t.key}`}
                          className={`flex flex-col items-center gap-1 p-2 rounded border text-xs transition ${
                            active
                              ? `bg-${t.color}-50 border-${t.color}-500 text-${t.color}-800 font-semibold`
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <Icon size={18} weight={active ? 'duotone' : 'regular'} />
                          {t.label.replace(' Vendor', '')}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name" /></div>
                <div><Label>Email (login)</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-email" /></div>
                <div>
                  <Label>Password</Label>
                  <div className="relative">
                    <Input type={showPass ? 'text' : 'password'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="vendor-password" />
                    <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700">
                      {showPass ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>Discount %</Label><Input type="number" step="0.01" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} data-testid="vendor-discount" /></div>
                </div>
                <Button onClick={createVendor} disabled={creating} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="vendor-create-submit">
                  {creating ? 'Creating...' : 'Create Vendor'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="overflow-x-auto">
        <Table className="table-dense">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Email</TableHead>
              <TableHead className="text-right">Discount</TableHead>
              <TableHead className="text-right">Credited</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance Owed</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {vendors.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-gray-500">No vendors yet</TableCell></TableRow>
            ) : vendors.map((v) => (
              <TableRow key={v.id} data-testid={`vendor-row-${v.id}`}>
                <TableCell className="font-medium">{v.name}</TableCell>
                <TableCell>{typeChip(v.vendor_type)}</TableCell>
                <TableCell className="text-xs text-gray-500">{v.email}</TableCell>
                <TableCell className="text-right text-sm">{v.discount_percent}%</TableCell>
                <TableCell className="text-right text-sm text-emerald-700">{fmt(v.credit)}</TableCell>
                <TableCell className="text-right text-sm text-red-700">{fmt(v.debit)}</TableCell>
                <TableCell className="text-right text-sm font-bold text-fuchsia-700">{fmt(v.balance)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/vendors/${v.id}`)} data-testid={`open-vendor-${v.id}`}>
                    Open
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-[10px] text-gray-400 mt-2">
        Vendors apna dashboard self-login karke dekh sakte hain. Admin hi account create karta hai aur fund payments record karta hai. CA sirf read-only.
      </p>
    </div>
  );
}
